import { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles } from '@/mocks/articles';
import { movies } from '@/mocks/movies';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  const article = id ? articles[id] : undefined;
  const relatedMovies = useMemo(() => {
    if (!article) return [];
    return article.relatedMovieIds
      .map((rid) => movies.find((m) => m.id === rid))
      .filter(Boolean);
  }, [article]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-error-warning-line text-4xl text-foreground-300"></i>
            </div>
            <h2 className="mt-4 text-xl font-heading font-bold text-foreground-900">内容未找到</h2>
            <p className="mt-2 text-foreground-500">该影片暂时不可用</p>
            <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600 font-medium cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    image: article.coverImage,
    datePublished: article.releaseDate,
    author: {
      '@type': 'Organization',
      name: '桃花影院',
    },
    publisher: {
      '@type': 'Organization',
      name: '桃花影院',
      logo: {
        '@type': 'ImageObject',
        url: 'https://taohua-cinema.com/logo.png',
      },
    },
  };

  const breadcrumbStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://taohua-cinema.com',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: article.category,
        item: `https://taohua-cinema.com/category/${article.categorySlug}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
        item: `https://taohua-cinema.com/article/${article.id}`,
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleStructuredData) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbStructuredData) }}
      />
      <Navbar />
      <main className="pt-20">
        <article className="w-full px-4 md:px-6 py-8">
          <div className="max-w-5xl mx-auto">
            <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-6 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
              <span>/</span>
              <Link to={`/category/${article.categorySlug}`} className="hover:text-primary-500 transition-colors cursor-pointer">{article.category}</Link>
              <span>/</span>
              <span className="text-foreground-700 font-medium">{article.title}</span>
            </nav>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-1">
                <div className="sticky top-24">
                  <div className="relative overflow-hidden rounded-lg aspect-[2/3] bg-background-100">
                    <img
                      src={article.coverImage}
                      alt={article.title}
                      title={`${article.title} - 桃花影院免费在线观看`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="mt-4 flex flex-col gap-2">
                    <Link
                      to={`/article/${article.id}`}
                      className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 text-white rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-play-fill text-base"></i>
                      </div>
                      立即播放
                    </Link>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-2">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
                  {article.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 mb-6">
                  <div className="flex items-center gap-1">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-star-fill text-accent-500 text-base"></i>
                    </div>
                    <span className="text-base font-semibold text-accent-500">{article.rating}</span>
                  </div>
                  <span className="text-sm text-foreground-400">{article.year}</span>
                  <span className="px-2 py-0.5 text-xs rounded-full bg-primary-100 text-primary-700 whitespace-nowrap">
                    {article.category}
                  </span>
                  {article.tags.map((tag) => (
                    <span key={tag} className="px-2 py-0.5 text-xs rounded-full bg-background-100 text-foreground-500 whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6 p-4 rounded-lg bg-background-100">
                  <div>
                    <span className="text-xs text-foreground-400">导演</span>
                    <p className="text-sm font-medium text-foreground-900">{article.director}</p>
                  </div>
                  <div>
                    <span className="text-xs text-foreground-400">主演</span>
                    <p className="text-sm font-medium text-foreground-900">{article.cast.join(' / ')}</p>
                  </div>
                  <div>
                    <span className="text-xs text-foreground-400">时长/集数</span>
                    <p className="text-sm font-medium text-foreground-900">{article.episodeCount || article.duration}</p>
                  </div>
                  <div>
                    <span className="text-xs text-foreground-400">地区</span>
                    <p className="text-sm font-medium text-foreground-900">{article.region}</p>
                  </div>
                  <div>
                    <span className="text-xs text-foreground-400">语言</span>
                    <p className="text-sm font-medium text-foreground-900">{article.language}</p>
                  </div>
                  <div>
                    <span className="text-xs text-foreground-400">上映日期</span>
                    <p className="text-sm font-medium text-foreground-900">{article.releaseDate}</p>
                  </div>
                </div>

                <div className="prose prose-sm max-w-none">
                  <h2 className="text-lg font-heading font-bold text-foreground-900 mb-3">剧情简介</h2>
                  {article.fullDescription.split('\n\n').map((paragraph, i) => (
                    <p key={i} className="text-sm text-foreground-600 leading-relaxed mb-3">
                      {paragraph}
                    </p>
                  ))}
                </div>

                <p className="text-xs text-foreground-400 mt-4">{article.updateInfo}</p>
              </div>
            </div>

            {relatedMovies.length > 0 && (
              <section className="mt-12 pt-8 border-t border-background-200/70">
                <h2 className="text-lg font-heading font-bold text-foreground-900 mb-4">相关推荐</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {relatedMovies.map((movie) => (
                    <Link
                      key={movie!.id}
                      to={`/article/${movie!.id}`}
                      className="group cursor-pointer"
                    >
                      <div className="relative overflow-hidden rounded-lg aspect-[2/3] bg-background-100">
                        <img
                          src={movie!.coverImage}
                          alt={movie!.title}
                          title={`${movie!.title} - 桃花影院`}
                          className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                          loading="lazy"
                        />
                      </div>
                      <h3 className="mt-2 text-sm font-semibold text-foreground-900 line-clamp-1 group-hover:text-primary-500 transition-colors">
                        {movie!.title}
                      </h3>
                      <div className="flex items-center gap-1 mt-0.5">
                        <div className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-star-fill text-accent-500 text-xs"></i>
                        </div>
                        <span className="text-xs text-accent-500">{movie!.rating}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}